// The resource client: the portal's data provider dialect, byte for byte
// (ADR 0006; portal: src/hooks/useDataProvider.ts). Nothing outside src/api/
// builds a URL.
import { parseHasNextPage, parseTotal, type ApiCapabilities } from './capabilities'
import { request, type HttpMethod } from './http'
import type { ListQuery, ListResult, RecordId } from './types'

export interface TenantOption {
  /** Sent as `X-TenantId`. Pass it only where the portal does (PARITY Q1). */
  tenantId?: string | number
  signal?: AbortSignal
}

export interface ActionInit {
  method?: HttpMethod
  body?: unknown
  query?: URLSearchParams | Record<string, string> | string
  tenantId?: string | number
  signal?: AbortSignal
}

/** What a list call accepts besides the query. */
export interface ListOptions {
  signal?: AbortSignal
  /** Defaults to API_CAPABILITIES (decides whether next-page headers are read). */
  capabilities?: ApiCapabilities
}

/**
 * `encodeURIComponent` plus `!'()*`, as query-string's `stringify` encodes
 * (strict mode, its default) — used for the JSON `filter=` dialect.
 */
function strictEncode(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
  )
}

function idPath(name: string, id: RecordId): string {
  return `${name}/${encodeURIComponent(String(id))}`
}

/**
 * The list query string, in the portal's key order: `sort, order, _start,
 * _end`, then the flat filters spread over them (portal: useDataProvider.ts:25-31).
 * Arrays are comma-joined as URLSearchParams does.
 *
 * Deviation: null, undefined and '' filter values are omitted. The portal
 * sends the strings "undefined"/"null" (PARITY "Data/getList filters").
 */
export function listSearchParams(query: ListQuery): URLSearchParams {
  const entries: Record<string, string> = {
    sort: query.sort ?? 'id',
    order: query.order ?? 'ASC',
    _start: String(query.start),
    _end: String(query.end)
  }
  for (const [key, value] of Object.entries(query.filter ?? {})) {
    if (value === null || value === undefined || value === '') continue
    entries[key] = String(value)
  }
  for (const [key, value] of Object.entries(query.params ?? {})) entries[key] = value
  return new URLSearchParams(entries)
}

/** The created record's id, from `json.data[0]`, `json.data` or `json` (portal: useDataProvider.ts:104-111). */
function createdId(json: unknown): RecordId | undefined {
  const obj = json as { data?: unknown } | null | undefined
  const data = obj?.data
  const created = (Array.isArray(data) ? data[0] : (data ?? json)) as
    { id?: RecordId } | null | undefined
  return created?.id ?? undefined
}

function unwrapOne(json: unknown): unknown {
  return Array.isArray(json) ? (json[0] as unknown) : json
}

export function resource<T = Record<string, unknown>>(name: string) {
  return {
    name,

    /** `GET /{res}?sort&order&_start&_end&<filters>`. */
    async list(query: ListQuery, options: ListOptions = {}): Promise<ListResult<T>> {
      const { json, headers } = await request<T[] | undefined>('GET', name, {
        query: listSearchParams(query),
        ...(options.signal && { signal: options.signal })
      })
      return {
        rows: Array.isArray(json) ? json : [],
        total: parseTotal(headers),
        hasNextPage: parseHasNextPage(headers, options.capabilities)
      }
    },

    /** `GET /{res}/{id}`; an array body is unwrapped to `[0]`. */
    async one(id: RecordId, options: TenantOption = {}): Promise<T> {
      const { json } = await request('GET', idPath(name, id), {
        ...(options.tenantId !== undefined && { tenantId: options.tenantId }),
        ...(options.signal && { signal: options.signal })
      })
      return unwrapOne(json) as T
    },

    /** `POST /{res}`, then `GET /{res}/{id}` for the full record (no X-TenantId, as the portal). */
    async create(data: Partial<T> | Record<string, unknown>): Promise<T> {
      const { json } = await request('POST', name, { body: { ...data } })
      const id = createdId(json)
      if (!id) throw new Error('No ID received on create response.')
      const { json: full } = await request('GET', idPath(name, id))
      // The portal spreads the raw body; an array body is unwrapped like getOne.
      return { ...(unwrapOne(full) as T), id }
    },

    /**
     * `PUT /{res}/{id}` with body `{id, ...data}`, but the URL id always wins
     * (PARITY Q8: the portal's `{id, ...data}` let an edited id override it).
     */
    async update(
      id: RecordId,
      data: Partial<T> | Record<string, unknown>,
      options: TenantOption = {}
    ): Promise<T> {
      // Key order as the portal's `{id, ...data}`; the value is always the URL id.
      const body: Record<string, unknown> = { id, ...data }
      body.id = id
      const { json } = await request<(T & { id?: RecordId }) | undefined>('PUT', idPath(name, id), {
        body,
        ...(options.tenantId !== undefined && { tenantId: options.tenantId })
      })
      return { ...json, id: json?.id ?? id } as T
    },

    /** `DELETE /{res}/{id}`. */
    async remove(id: RecordId, options: TenantOption = {}): Promise<unknown> {
      const { json } = await request('DELETE', idPath(name, id), {
        ...(options.tenantId !== undefined && { tenantId: options.tenantId })
      })
      return json
    },

    /** `GET /{res}?filter={"ids":[…]}`, query-string encoded (PARITY Q23). */
    async getMany(ids: readonly RecordId[], options: ListOptions = {}): Promise<T[]> {
      const { json } = await request<T[] | undefined>('GET', name, {
        query: `filter=${strictEncode(JSON.stringify({ ids }))}`,
        ...(options.signal && { signal: options.signal })
      })
      return Array.isArray(json) ? json : []
    },

    /** A custom call under the resource: `{method} /{res}/{path}`. */
    async action<R = unknown>(path: string, init: ActionInit = {}): Promise<R> {
      const { method = 'POST', ...rest } = init
      const { json } = await request<R>(method, `${name}/${path.replace(/^\//, '')}`, rest)
      return json
    }
  }
}

export type Resource<T> = ReturnType<typeof resource<T>>
